"use client";

import React from "react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { CircleQuestionMark as HelpCircle } from 'lucide-react';
interface FaqItem {
  id: string;
  question: string;
  answer: string;
}
const faqs: FaqItem[] = [{
  id: "item-1",
  question: "How do you preserve historic trim, mouldings, and architectural character during renovations?",
  answer: "We document and carefully label all original heart pine trim, picture rails, crown moulding, and baseboards before demolition begins. Salvageable woodwork is numbered, stored safely, and reconditioned or matched with custom millwork profiles where walls or doorways are reconfigured."
}, {
  id: "item-2",
  question: "How are plaster walls and ceilings handled when opening floor plans?",
  answer: "Historic Decatur homes often combine lath-and-plaster walls with original framing spans. When modifying or opening partition walls, we install structural steel or LVL headers engineered for existing loads, and carefully transition adjoining plaster into modern drywall with skim-coat blending."
}, {
  id: "item-3",
  question: "What happens when upgrading plumbing and electrical in older craftsman homes?",
  answer: "Older residences frequently retain legacy galvanized piping, cast-iron stacks, or ungrounded wiring. Our interior remodels bring services up to current code standard—running dedicated circuits for modern kitchens and bathrooms while routing through crawl spaces or chase walls to protect untouched ceilings."
}, {
  id: "item-4",
  question: "Can we relocate a kitchen or primary bathroom without losing historic integrity?",
  answer: "Yes. Relocating wet rooms requires careful planning around existing floor joists, ductwork pathways, and exterior window placement. We design layouts that modernize circulation and natural lighting while respecting original room proportions and exterior facade balance."
}, {
  id: "item-5",
  question: "Can our family remain in the home during an extensive interior remodel?",
  answer: "Phased interior remodeling allows families to stay in unimpacted zones of the home. We establish negative air machines, zippered dust barriers, and designated construction entry paths to protect living quarters and preserve indoor air quality throughout structural and finish work."
}];
export default function InteriorRemodelingFaq() {
  return <section id="interiorremodelingfaq" data-nav="light" className="relative bg-background text-foreground py-20 md:py-28 px-4 sm:px-6 lg:px-8 overflow-hidden">
      <div className="max-w-3xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-secondary text-secondary-foreground text-xs font-medium tracking-wide mb-4">
            <HelpCircle className="size-3.5 text-chart-1" />
            <span>Decatur Renovation Guide</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-medium tracking-tight text-foreground text-balance">
            Interior Remodeling in Older Homes
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            Essential considerations for structural modifications, historic woodwork preservation, and modernization in classic Decatur residences.
          </p>
        </div>

        {/* Q&A Accordion */}
        <div className="bg-card/70 backdrop-blur-sm border border-border rounded-xl shadow-sm p-4 sm:p-6 md:p-8">
          <Accordion type="single" collapsible defaultValue="item-1" className="w-full space-y-3">
            {faqs.map((faq, index) => <AccordionItem key={faq.id} value={faq.id} data-index={index} className="border border-border/80 rounded-lg px-4 sm:px-5 py-1 bg-background/50 hover:bg-background/80 transition-colors last:border-b">
                <AccordionTrigger className="text-left font-serif text-base sm:text-lg font-medium text-foreground hover:no-underline py-4">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="font-default text-muted-foreground text-sm sm:text-base leading-relaxed pt-1 pb-4">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>)}
          </Accordion>
        </div>
      </div>
    </section>;
}